<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import ChevronLeft from "@/assets/icons/chevron_left.svg?component"
import ChevronRight from "@/assets/icons/chevron_right.svg?component"
import { ref, watch, onMounted } from "vue"
import type { TabSettingsType } from "./TabGroupInterface"
import { useScroll } from "@vueuse/core"
import { addReportWindowSizeCallback } from "@/utils/screen"
import Button from "@/components/Button/Button.vue"
import { ButtonType, ButtonVariant } from "../Button/ButtonEnums"

const props = defineProps<{
   tabSettings: TabSettingsType[]
   selectedName?: string
}>()

const emit = defineEmits<{
   (e: "tab-selected", value: TabSettingsType): void
}>()

const selected = ref(props.tabSettings[0].name),
   tabGroup = ref<HTMLDivElement>(),
   tabGroupHeader = ref<HTMLDivElement | null>(null),
   { x, arrivedState, isScrolling } = useScroll(tabGroupHeader, {
      behavior: "smooth"
   }),
   leftButtonDisabled = ref(true),
   rightButtonDisabled = ref(true),
   arrowStyle = ref({
      display: "none",
      "align-self": "center"
   })

defineExpose({
   selected
})

watch(
   () => props.selectedName,
   (newValue) => {
      if (
         props.selectedName &&
         props.tabSettings.find((e) => e.name == newValue)
      ) {
         selected.value = newValue || ""
      } else {
         selected.value = props.tabSettings[0].name
      }
   },
   {
      deep: true,
      immediate: true
   }
)

onMounted(() => {
   showHideArrows()
   addReportWindowSizeCallback(showHideArrows)
})

const selectTab = (tab: TabSettingsType, ev: Event): void => {
   emit("tab-selected", tab)
   selected.value = tab.name
   ;(ev.target as HTMLDivElement).scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
   })
}

const showHideArrows = () => {
   const width = tabGroup.value?.clientWidth || 0,
      headerWidth =
         tabGroup.value?.querySelector(".tab-group-header")?.scrollWidth || 0

   if (headerWidth > width) {
      rightButtonDisabled.value = false
      arrowStyle.value.display = "flex"
   } else {
      arrowStyle.value.display = "none"
   }
}

const scroll = (dir: number) => {
   const scrollWidthSize = (tabGroup.value?.clientWidth || 300) / 2

   if (dir > 0) {
      x.value += scrollWidthSize
   } else {
      x.value -= scrollWidthSize
   }
}

watch(isScrolling, () => {
   leftButtonDisabled.value = arrivedState.left
   rightButtonDisabled.value = arrivedState.right
})
</script>

<template>
   <div ref="tabGroup" class="tab-group">
      <div style="display: flex; align-items: center">
         <div :style="arrowStyle" class="tab-group--arrow" @click="scroll(-1)">
            <Button
               :type="ButtonType.Link"
               :variant="ButtonVariant.Icon"
               :disabled="leftButtonDisabled"
               :round="true"
            >
               <ChevronLeft />
            </Button>
         </div>
         <div ref="tabGroupHeader" class="tab-group-header">
            <div
               v-for="tab in props.tabSettings"
               :key="tab.name + '_header'"
               class="tab-group-header-item"
               :class="tab.name == selected ? 'tab-selected' : ''"
               @click="selectTab(tab, $event)"
            >
               {{ tab.label }}
            </div>
         </div>
         <div :style="arrowStyle" class="tab-group--arrow" @click="scroll(1)">
            <Button
               :type="ButtonType.Link"
               :variant="ButtonVariant.Icon"
               :disabled="rightButtonDisabled"
               :round="true"
            >
               <ChevronRight />
            </Button>
         </div>
      </div>
      <div class="tab-group-body">
         <div
            v-for="tab in props.tabSettings"
            :id="tab.name"
            :key="tab.name + '_body'"
            :class="['tab-body', tab.name == selected ? 'tab-visible' : '']"
         >
            <slot :name="tab.name"></slot>
         </div>
      </div>
   </div>
</template>
